// hooks/useFormViewerWizard/completeWizard.ts — Step 3: kunci ongkir + bayar
// via PATCH + efek sukses (CAPI/funnel/WA) + path thanks.
import { buildOrderPayload } from '../useFormViewer/submitPayload';
import { runSubmitGuards } from '../useFormViewer/submitGuards';
import { fireCapiEvents } from '../useFormViewer/submitCapi';
import { fireSubmitFunnelEvent } from '../useFormViewer/submitFunnel';
import { sendOrderConfirmations } from '../useFormViewer/submitConfirmation';
import { patchWizardOrder } from './patchWizardOrder';
import { buildSplitPatch } from './buildSplitPatch';
import { supabase } from '../../../../supabase/client';
import { getStoredPhoneLast4 } from '../../../../hooks/useThankYouPage/fetchOrder';

export async function completeWizardOrder(args: {
    hook: any; form: any; orderId: string; assignedCsId: string | null;
    minMid: number; sendOrderConfirmation: any;
}): Promise<{ thanksPath: string }> {
    const { hook, form, orderId, assignedCsId, minMid, sendOrderConfirmation } = args;
    if (!orderId) throw new Error('Pesanan step-1 belum tercatat — ulangi dari langkah 1.');
    if (!hook.selectedPaymentKey) throw new Error('Pilih metode pembayaran dulu.');
    // Guard balapan (wizardAutoComplete sweep, 2026-10-21): order sudah dilengkapi/
    // diproses otomatis (sumber bukan wizard_step1 / status bukan Pending) → JANGAN
    // timpa pilihan sistem/CS — arahkan ke thanks, data sistem yang menang.
    try {
        const { data: live } = await supabase.from('orders').select('status,sourceForm').eq('id', orderId).maybeSingle();
        if (live && (live.status !== 'Pending' || (live.sourceForm && live.sourceForm !== 'wizard_step1' && live.sourceForm !== 'wizard_complete'))) {
            const slug = form?.slug || '';
            throw { thanksPath: `/f/${slug}/thanks/${orderId}${window.location.search}`, autoDone: true };
        }
    } catch (e: any) {
        if (e?.thanksPath) throw e;
        /* cek gagal = lanjut seperti biasa (submit yang memutuskan) */
    }

    const isCODVal = ['cod', 'codJawaBali', 'codLuarJawaBali'].includes(hook.selectedPaymentKey as string);
    const guardErr = await runSubmitGuards({
        form, addressData: hook.addressData, selectedPaymentKey: hook.selectedPaymentKey,
        selectedDuitkuMethod: hook.selectedDuitkuMethod, selectedEverproRate: hook.selectedEverproRate,
        selectedBiteshipRate: hook.selectedBiteshipRate, shippingCost: hook.shippingCost,
        isCODVal, minMid, brandEverpro: hook.brandEverpro, brandMengantar: hook.brandMengantar, brandLincah: (hook as any).brandLincah,
        rateHold: !!(hook.rateHoldRef?.current), lowPerfNoCod: !!(hook.lowPerfNoCod),
        ratesSettled: (hook as any).ratesSettledRef?.current,
        selectedShippingKey: hook.selectedShippingKey,
    });
    if (guardErr) throw new Error(guardErr);

    const built = await buildOrderPayload({
        form, customerData: hook.customerData, addressData: hook.addressData,
        selectedOptions: hook.selectedOptions, selectedShippingKey: hook.selectedShippingKey,
        selectedPaymentKey: hook.selectedPaymentKey, selectedDuitkuMethod: hook.selectedDuitkuMethod,
        selectedEverproRate: hook.selectedEverproRate, selectedBiteshipRate: hook.selectedBiteshipRate,
        currentCombination: hook.currentCombination, linkedProductName: hook.linkedProductName || '',
        quantity: hook.quantity, shippingCost: hook.shippingCost, codFee: hook.codFee,
        orderId, uniqueCode: 1, isCODVal, isSimpleEmbed: false, herbalClaimMode: undefined,
        discountAmount: hook.discountAmount || 0, voucherCode: hook.couponApplied?.code || '',
        sourceFormOverride: 'wizard_complete',
    });
    const p = built.pOrder;
    const split = buildSplitPatch(hook.addressData);
    try {
    await patchWizardOrder(orderId, {
        province: p.province, city: p.city, district: p.district, village: p.village,
        provinceId: (p as any).provinceId || (hook.addressData as any)?.provinceId || undefined,
        cityId: (p as any).cityId || (hook.addressData as any)?.cityId || undefined,
        districtId: (p as any).districtId || (hook.addressData as any)?.districtId || undefined,
        villageId: (p as any).villageId || (hook.addressData as any)?.villageId || undefined,
        postalCode: p.postalCode, shippingAddress: p.shippingAddress,
        address_detail: split.address_detail, address_rtrw: split.address_rtrw,
        address_landmark: split.address_landmark,
        ai_formatted_address: split.ai_formatted_address,
        // ATURAN 2026-10-30: wizard = wilayah otoritatif frontend. Tandai analyzed
        // agar sweep/poll tidak menganggap belum dianalisis lalu menimpa dengan
        // halu Gemini (kasus b175e656: Jakarta 11140 → Maluku 97612). Tanpa kurangi.
        ai_province: p.province || undefined,
        ai_city: p.city || undefined,
        ai_district: p.district || undefined,
        ai_village: p.village || undefined,
        ai_analyzed_at: new Date().toISOString(),
        ai_address_validated: true,
        shippingCost: p.shippingCost, shippingOriginalCost: p.shippingOriginalCost,
        shippingSubsidy: p.shippingSubsidy, shippingMethod: p.shippingMethod,
        courier: p.courier, logistic_code: p.logistic_code, rate_code: p.rate_code,
        logistic_logo_url: p.logistic_logo_url, everpro_signed_key: p.everpro_signed_key,
        shipping_provider: p.shipping_provider, everpro_min_duration: p.everpro_min_duration,
        everpro_max_duration: p.everpro_max_duration, paymentMethod: p.paymentMethod,
        paymentGateway: p.paymentGateway, codFee: p.codFee, totalPrice: p.totalPrice,
        quantity: p.quantity, sourceForm: 'wizard_complete',
    });
    } catch (e: any) {
        // Kalah balapan dgn sweep/CS tepat di antara cek-live dan PATCH final →
        // JANGAN toast error: order masih ada = ikut thanks dgn data sistem.
        if (String(e?.message || '').includes('sudah tidak bisa dilengkapi')) {
            try {
                const last4 = getStoredPhoneLast4(orderId) || undefined;
                const { data } = await supabase
                    .rpc('get_public_order_limited', { p_order_id: orderId, p_phone_last4: last4 })
                    .abortSignal(AbortSignal.timeout(10000));
                const row: any = Array.isArray(data) ? data[0] : data;
                if (row?.id) {
                    const slug = form?.slug || '';
                    throw { thanksPath: `/f/${slug}/thanks/${orderId}${window.location.search}`, autoDone: true };
                }
            } catch (inner: any) { if (inner?.thanksPath) throw inner; /* jatuh ke error asli */ }
        }
        throw e;
    }

    const orderTotal = Number(p.totalPrice) || 0;
    fireCapiEvents({
        globalTrackingSettings: hook.globalTrackingSettings, form, phone: built.phone,
        email: built.email, name: built.name, sellingPrice: built.sellingPrice,
        shippingCost: hook.shippingCost, codFee: hook.codFee, productName: built.productName,
        orderData: { id: orderId }, orderValue: orderTotal,
        customerCity: built.city, customerProvince: built.province, customerPostalCode: p.postalCode || undefined,
    });
    fireSubmitFunnelEvent(form, hook.globalTrackingSettings, orderId, {
        orderTotal, email: built.email, phone: built.phone, name: built.name,
    });
    await sendOrderConfirmations({
        orderData: { id: orderId }, form, brandId: built.brandId, assignedCsId: assignedCsId || undefined,
        phone: built.phone, name: built.name, productName: built.productName, totalPrice: orderTotal,
        addressText: built.addressText, province: built.province, city: built.city,
        district: built.district, village: built.village, sendOrderConfirmation,
    });
    const slug = form?.slug || '';
    const thanksPath = form?.thankYouPage?.verificationEnabled
        ? `/f/${slug}/confirm/${orderId}${window.location.search}` : `/f/${slug}/thanks/${orderId}${window.location.search}`;
    return { thanksPath };
}
